import { useEffect, useState, useRef, useMemo } from 'react';
import { v4 } from 'uuid';
import ReactPlayer from 'react-player'
import { track } from 'utils/index';
import {recordPlaybackEvent} from 'api/index';
import { useAppContext } from 'contexts/AppContext';
import LineLoader from '../LineLoader/LineLoader';
import ResolutionSwitcher from '../ResolutionSwitcher/ResolutionSwitcher';
import styles from './VideoPlayer.module.scss';

const eventsToTrack = ['play', 'pause', 'seeked', 'ended']

const VideoPlayer = ({video, controls=true, contextClickedPlay=false, contextSetClickedPlay=false}) => {

    const playbackSessionId = useMemo(() => v4(), []);
    const [show, setShow] = useState({playButton: false})
    const playerRef = useRef()
    const { isMobile, xoSessionId } = useAppContext()
    const [activeResolution, setActiveResolution] = useState("fourEighty")
    const initialPlayerState = {
        loading: false,
        url: video.is_aws ? video.cloudfront_url : video[activeResolution],
        clicked: true,
        playing: true,
        controls,
        secondsPlayed: 0,
        hovering: false,
        config: video.is_aws ? {
            file: {
                hlsOptions: {
                    forceHLS: true,
                },
            }
        } : {}
    }
    const [player, setPlayer] = useState(initialPlayerState)
    const lastEventTracked = useRef(null)
    const currentVideoTime = useRef(0)

    useEffect(()=>{
        const timeoutId = setTimeout(() => {
                setShow({...show, playButton:true})
            }, 200);
        return () => clearTimeout(timeoutId)
    }, [])

    useEffect(()=>{
       if(playerRef.current){
         const videoElement = document.querySelector('#videoplayer video')
         if(videoElement){

            eventsToTrack.forEach(event => {
                videoElement.addEventListener(event, (e) => {
                    const currentTime = Math.round((e.target.currentTime + Number.EPSILON) * 10) / 10
                    
                    if(e.type === 'pause'){
                        const lastRecordedTime = currentVideoTime.current
                        const thisTime = currentTime
                        if(Math.abs(lastRecordedTime - thisTime) > 1){
                            return
                        }
                    }

                    if (e.type !== lastEventTracked.current) {
                        const event = {
                            name: e.type,
                            xo_session_id: xoSessionId,
                            playback_session_id: playbackSessionId,
                            video_id: video.video_id,
                            video_title: video.title,
                            video_duration: video.length,
                            sound_level: videoElement.volume,
                            full_screen: videoElement.webkitDisplayingFullscreen,
                            current_time: currentTime,
                        }
                        lastEventTracked.current = e.type
                        if(e.type === 'seeked'){
                            if(currentVideoTime.current === currentTime) return
                            event.seeked_from = currentVideoTime.current
                            event.seeked_to = currentTime
                            currentVideoTime.current = currentTime
                        }
                        track(event.name, {
                            ...event,
                        })
                        recordPlaybackEvent(event)
                    }
                })
            }
        )
        videoElement.addEventListener('timeupdate', (e)=>{
                const from = currentVideoTime.current
                const to = e.target.currentTime
                if(Math.abs(from - to) > 1){
                    return
                }
                currentVideoTime.current = // round e.target.currentTime to 1 decimal
                Math.round((e.target.currentTime + Number.EPSILON) * 10) / 10
            
        })
    }
       }
    }, [playerRef.current])

    useEffect(()=>{
        if(player.clicked){
            setPlayer({...player, playing: true})
            contextSetClickedPlay && contextSetClickedPlay(true)
        }
    }, [player.clicked])

    useEffect(()=>{
        if(contextClickedPlay) setPlayer({...player, clicked: true, playing: contextClickedPlay})
    }, [contextClickedPlay])

    function handleVideoPlayerClick(e){
        e.preventDefault()
        const mobilePlaying = !player.clicked ? true : player.playing
        const desktopPlaying = !player.playing
        const playing = isMobile ? mobilePlaying : desktopPlaying
        setPlayer({...player, clicked: true, playing, isHardPaused: player.playing})
    }

    return (

        <div className={styles.videoPlayer} id="videoplayer">

            <div className={`${styles.videoPlayer__video} ${!player.clicked ? styles['videoPlayer__video-hidden'] : styles['videoPlayer__video-showing']}`} onClick={handleVideoPlayerClick}>
                {player.loading &&
                <>
                    <LineLoader />
                </>
                }
                <div className={`${styles.videoPlayer__player}`} onMouseEnter={()=>setPlayer({...player, hovering: true})} onMouseLeave={()=>setPlayer({...player, hovering: false})} id="videoPlayerContainer">
                    <div>
                        <ReactPlayer
                        url={player.url}
                        playing={player.playing}
                        controls={player.controls}
                        config={player.config}
                        width="100%"
                        height="100%"
                        ref={playerRef}
                        playsinline={true}
                        onProgress={(progress) => {
                            setPlayer({...player, secondsPlayed: progress.playedSeconds});
                        }}
                        />
                        {!video.is_aws && 
                        <div className={styles.videoPlayer__resolutionSwitcher}>
                            <ResolutionSwitcher player={player} activeResolution={activeResolution} setActiveResolution={setActiveResolution} />
                        </div>
                        }
                    </div>
                </div>
            </div>
        </div>
     );
}

export default VideoPlayer;